// frontend/src/contexts/InternalStaffContext.js
// 自社スタッフ (Internal Staff) のセッション管理
//
// 管理する状態:
//   - internalStaff: ログイン中の自社スタッフ情報 (id, name, role)
//   - selectedOrg: 閲覧対象として選択中の組織 (id, name)
//   - editSession: アクティブな編集セッション (id, reason, expires_at) or null
//
// 提供する操作:
//   - login / logout: Supabase Auth + internal_staff 検証
//   - selectOrganization: 組織を閲覧モードで開始
//   - startEditMode: 編集モード ON (作業理由必須、 2h 自動失効)
//   - endEditMode: 編集モード OFF
//   - logAudit: 監査ログ書き込み
//   - canEdit: 現在編集できるか (= editSession が有効か)

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabase';

const InternalStaffContext = createContext(null);

export const useInternalStaff = () => {
  const ctx = useContext(InternalStaffContext);
  if (!ctx) throw new Error('useInternalStaff は InternalStaffProvider 内で使う必要があります');
  return ctx;
};

const STORAGE_KEY_ORG = 'internal_selected_org';
const STORAGE_KEY_SESSION = 'internal_edit_session';

export const InternalStaffProvider = ({ children }) => {
  const [internalStaff, setInternalStaff] = useState(null);
  const [selectedOrg, setSelectedOrgState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_ORG);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [editSession, setEditSessionState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SESSION);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // 期限切れチェック
      if (new Date(parsed.expires_at) <= new Date()) {
        localStorage.removeItem(STORAGE_KEY_SESSION);
        return null;
      }
      return parsed;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const editSessionTimerRef = useRef(null);

  // localStorage 同期
  useEffect(() => {
    if (selectedOrg) localStorage.setItem(STORAGE_KEY_ORG, JSON.stringify(selectedOrg));
    else localStorage.removeItem(STORAGE_KEY_ORG);
  }, [selectedOrg]);

  useEffect(() => {
    if (editSession) localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(editSession));
    else localStorage.removeItem(STORAGE_KEY_SESSION);
  }, [editSession]);

  // 編集セッションの自動失効タイマー
  useEffect(() => {
    if (editSessionTimerRef.current) clearTimeout(editSessionTimerRef.current);
    if (!editSession) return;
    const expiresAt = new Date(editSession.expires_at).getTime();
    const remainMs = expiresAt - Date.now();
    if (remainMs <= 0) {
      setEditSessionState(null);
      return;
    }
    editSessionTimerRef.current = setTimeout(() => {
      console.log('🔒 編集セッションが自動失効しました');
      setEditSessionState(null);
    }, remainMs);
    return () => {
      if (editSessionTimerRef.current) clearTimeout(editSessionTimerRef.current);
    };
  }, [editSession]);

  // 現在の Supabase Auth ユーザーから internal_staff を取得
  const refreshInternalStaff = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setInternalStaff(null);
        return null;
      }
      const { data, error } = await supabase
        .from('internal_staff')
        .select('id, user_id, email, name, role, is_active')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();
      if (error) {
        console.warn('internal_staff 取得エラー:', error.message);
        setInternalStaff(null);
        return null;
      }
      setInternalStaff(data);
      return data;
    } catch (e) {
      console.error('refreshInternalStaff:', e);
      setInternalStaff(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 初期化 + Auth 変更監視
  useEffect(() => {
    refreshInternalStaff();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      refreshInternalStaff();
    });
    return () => subscription?.unsubscribe();
  }, [refreshInternalStaff]);

  // 監査ログ書き込み
  const logAudit = useCallback(async (actionType, options = {}) => {
    if (!internalStaff) return;
    try {
      await supabase.from('internal_audit_logs').insert({
        internal_staff_id: internalStaff.id,
        organization_id: options.organizationId || selectedOrg?.id || '__none__',
        action_type: actionType,
        action_target: options.target || null,
        action_detail: options.detail || null,
        reason: options.reason || null,
      });
    } catch (e) {
      console.warn('audit log 書込失敗:', e.message);
    }
  }, [internalStaff, selectedOrg]);

  // ログアウト
  //   🔥 supabase_user / currentOrganization も明示クリア。
  //   internal モード中にこれらを偽装書き換えしているため、ログアウト時に確実に消す。
  const logout = useCallback(async () => {
    if (internalStaff) await logAudit('logout');
    await supabase.auth.signOut();
    setInternalStaff(null);
    setSelectedOrgState(null);
    setEditSessionState(null);
    localStorage.removeItem('supabase_user');
    localStorage.removeItem('currentOrganization');
    // 完全リセットのためログイン画面へハードリダイレクト
    window.location.href = '/internal/login';
  }, [internalStaff, logAudit]);

  // 組織選択 (= 閲覧モード開始)
  //   🔥 OrganizationContext / Header / 既存ページとの互換のため supabase_user を書き換え、
  //   その上で OrganizationProvider を再初期化させるためハードリロードする。
  //   これで自社スタッフが「普通に admin としてログインした時と全く同じ画面・体験」になる。
  const selectOrganization = useCallback(async (org) => {
    if (!org?.id) throw new Error('org.id が必要');
    // 🔥 window.location.href での画面遷移との race condition を避けるため、
    //    setSelectedOrgState の useEffect に依存せず、localStorage を直接書く。
    localStorage.setItem(STORAGE_KEY_ORG, JSON.stringify(org));
    localStorage.removeItem(STORAGE_KEY_SESSION); // 組織切替で編集セッションはリセット
    setSelectedOrgState(org);
    setEditSessionState(null);
    await logAudit('select_org', {
      organizationId: org.id,
      detail: { org_name: org.name },
    });
    // supabase_user を「該当組織の admin」として偽装 (Header / OrganizationContext が読む)
    localStorage.setItem('supabase_user', JSON.stringify({
      name: `🔧 Internal (${org.name || org.id})`,
      role: 'admin',
      organizationId: org.id,
      organizationName: org.name || org.id,
      organizationType: 'hospital',
    }));
    localStorage.setItem('currentOrganization', JSON.stringify({
      id: org.id, name: org.name || org.id, type: 'hospital',
    }));
  }, [logAudit]);

  // 編集モード ON
  //   reason 必須。 2 時間で自動失効。
  const startEditMode = useCallback(async (reason) => {
    if (!internalStaff) throw new Error('未ログイン');
    if (!selectedOrg) throw new Error('組織未選択');
    if (internalStaff.role === 'viewer') {
      throw new Error('viewer ロールは編集できません');
    }
    if (!reason || !reason.trim()) {
      throw new Error('作業理由を入力してください');
    }
    const expiresAt = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('internal_edit_sessions')
      .insert({
        internal_staff_id: internalStaff.id,
        organization_id: selectedOrg.id,
        reason: reason.trim(),
        expires_at: expiresAt,
      })
      .select()
      .single();
    if (error) throw error;
    setEditSessionState(data);
    await logAudit('edit_session_start', {
      organizationId: selectedOrg.id,
      reason: reason.trim(),
      detail: { session_id: data.id, expires_at: expiresAt },
    });
    return data;
  }, [internalStaff, selectedOrg, logAudit]);

  // 編集モード OFF (手動)
  const endEditMode = useCallback(async () => {
    if (!editSession) return;
    try {
      await supabase
        .from('internal_edit_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', editSession.id);
    } catch (e) {
      console.warn('edit session 終了失敗:', e.message);
    }
    await logAudit('edit_session_end', {
      organizationId: editSession.organization_id,
      detail: { session_id: editSession.id, manual: true },
    });
    setEditSessionState(null);
  }, [editSession, logAudit]);

  // 編集できるか
  const canEdit = !!(
    internalStaff
    && selectedOrg
    && editSession
    && editSession.organization_id === selectedOrg.id
    && new Date(editSession.expires_at) > new Date()
  );

  const value = {
    internalStaff,
    selectedOrg,
    editSession,
    canEdit,
    loading,
    refreshInternalStaff,
    selectOrganization,
    startEditMode,
    endEditMode,
    logout,
    logAudit,
  };

  return (
    <InternalStaffContext.Provider value={value}>
      {children}
    </InternalStaffContext.Provider>
  );
};
